import React, { useEffect, useState, useCallback } from 'react';
import { DeliveryZonePoint } from '../../../../redux/deliverySlice';

interface DrawingManagerProps {
  map: google.maps.Map;
  isDrawing: boolean;
  deliveryZone: DeliveryZonePoint[];
  onPolygonComplete: (polygon: DeliveryZonePoint[]) => void;
  onDrawingModeChange: (isDrawing: boolean) => void;
  onPolygonEdit?: (polygon: DeliveryZonePoint[]) => void;
}

const DrawingManager: React.FC<DrawingManagerProps> = ({ 
  map, 
  isDrawing, 
  deliveryZone,
  onPolygonComplete, 
  onDrawingModeChange,
  onPolygonEdit 
}) => {
  const [drawingManager, setDrawingManager] = useState<google.maps.drawing.DrawingManager | null>(null);
  const [existingPolygon, setExistingPolygon] = useState<google.maps.Polygon | null>(null);
  const [polygonListeners, setPolygonListeners] = useState<google.maps.MapsEventListener[]>([]);

  // Initialize drawing manager
  useEffect(() => {
    if (!map) return;

    const manager = new google.maps.drawing.DrawingManager({
      drawingMode: isDrawing ? google.maps.drawing.OverlayType.POLYGON : null,
      drawingControl: false,
      polygonOptions: {
        fillColor: '#FF0000',
        fillOpacity: 0.35,
        strokeWeight: 2,
        strokeColor: '#FF0000',
        strokeOpacity: 0.8,
        clickable: true,
        editable: true,
        draggable: true,
        zIndex: 1,
      },
    });

    manager.setMap(map);
    setDrawingManager(manager);

    // Add listener for polygon completion
    const polygonCompleteListener = google.maps.event.addListener(
      manager,
      'polygoncomplete',
      (polygon: google.maps.Polygon) => {
        // Remove existing polygon when drawing a new one
        if (existingPolygon) {
          existingPolygon.setMap(null);
          // Remove all listeners
          polygonListeners.forEach(listener => google.maps.event.removeListener(listener));
          setPolygonListeners([]);
        }

        const path = polygon.getPath();
        const coordinates: DeliveryZonePoint[] = [];
        
        for (let i = 0; i < path.getLength(); i++) {
          const point = path.getAt(i);
          coordinates.push({
            latitude: point.lat(),
            longitude: point.lng()
          });
        }
        
        // Update the existing polygon reference
        setExistingPolygon(polygon);
        
        // Pass only the delivery zone points (fixed signature)
        onPolygonComplete(coordinates);
        onDrawingModeChange(false);
        
        // Set up listeners for editing the new polygon
        setupPolygonListeners(polygon);
      }
    );

    return () => {
      google.maps.event.removeListener(polygonCompleteListener);
      manager.setMap(null);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, onPolygonComplete, onDrawingModeChange]);

  // Update drawing mode when isDrawing changes
  useEffect(() => {
    if (drawingManager) {
      drawingManager.setDrawingMode(
        isDrawing ? google.maps.drawing.OverlayType.POLYGON : null
      );
    }
  }, [isDrawing, drawingManager]);

  // Create or update existing polygon when deliveryZone changes
  useEffect(() => {
    if (!map) return;

    // Remove existing polygon if any
    if (existingPolygon) {
      existingPolygon.setMap(null);
      // Remove all listeners
      polygonListeners.forEach(listener => google.maps.event.removeListener(listener));
      setPolygonListeners([]);
      setExistingPolygon(null);
    }

    // Only create polygon if deliveryZone has points
    if (deliveryZone.length === 0) return;

    // Create new polygon from deliveryZone data
    const polygon = new google.maps.Polygon({
      paths: deliveryZone.map(point => new google.maps.LatLng(point.latitude, point.longitude)),
      strokeColor: '#FF0000',
      strokeOpacity: 0.8,
      strokeWeight: 2,
      fillColor: '#FF0000',
      fillOpacity: 0.35,
      editable: true,
      draggable: true,
    });

    polygon.setMap(map);
    setExistingPolygon(polygon);

    // Set up listeners for editing the polygon
    setupPolygonListeners(polygon);

    // Fit map to show the entire polygon
    const bounds = new google.maps.LatLngBounds();
    deliveryZone.forEach(point => {
      bounds.extend(new google.maps.LatLng(point.latitude, point.longitude));
    });
    
    // Add some padding to the bounds
    const extendedBounds = new google.maps.LatLngBounds(
      new google.maps.LatLng(bounds.getSouthWest().lat() - 0.01, bounds.getSouthWest().lng() - 0.01),
      new google.maps.LatLng(bounds.getNorthEast().lat() + 0.01, bounds.getNorthEast().lng() + 0.01)
    );
    
    map.fitBounds(extendedBounds);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliveryZone, map]);

  // Set up listeners for polygon editing events
  const setupPolygonListeners = useCallback((polygon: google.maps.Polygon) => {
    // Clear previous listeners
    polygonListeners.forEach(listener => google.maps.event.removeListener(listener));
    
    const listeners: google.maps.MapsEventListener[] = [];
    
    // Listen for insert/remove events on the polygon path
    const path = polygon.getPath();
    
    const insertListener = path.addListener('insert_at', () => {
      handlePolygonEdit(polygon);
    });
    
    const removeListener = path.addListener('remove_at', () => {
      handlePolygonEdit(polygon);
    });
    
    // Listen for set events (moving points)
    const setListener = path.addListener('set_at', () => {
      handlePolygonEdit(polygon);
    });
    
    // Listen for drag events
    const dragListener = google.maps.event.addListener(polygon, 'dragend', () => {
      handlePolygonEdit(polygon);
    });
    
    listeners.push(insertListener, removeListener, setListener, dragListener);
    setPolygonListeners(listeners);
    
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle polygon edit events
  const handlePolygonEdit = useCallback((polygon: google.maps.Polygon) => {
    const path = polygon.getPath();
    const coordinates: DeliveryZonePoint[] = [];
    
    for (let i = 0; i < path.getLength(); i++) {
      const point = path.getAt(i);
      coordinates.push({
        latitude: point.lat(),
        longitude: point.lng()
      });
    }
    
    // Notify parent component about the edit
    if (onPolygonEdit) {
      onPolygonEdit(coordinates);
    }
  }, [onPolygonEdit]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      // Remove all listeners
      polygonListeners.forEach(listener => google.maps.event.removeListener(listener));
      
      // Remove polygons from map
      if (existingPolygon) {
        existingPolygon.setMap(null);
      }
      
      if (drawingManager) {
        drawingManager.setMap(null);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
};

export default DrawingManager;